import React from "react";
import { ListChecks } from "lucide-react";
import GlobalDetailDrawer from "modules/swiftboard/components/GlobalDetailDrawer";
import LocalDateTime from "services/date-converter";
import { TRIGGER_TYPE_LABEL } from "../../../features/builder.constants";
import { formatValue } from "../../../features/builder.utils";
import StatusPill from "../../components/StatusPill";
import { CASE_STATUS, CHANNEL_LABEL, DELIVERY_STATUS, SKIP_REASON_LABEL } from "../../features/monitor.constants";
import { closeReasonText, subjectKeyText } from "../../features/monitor.utils";

const when = (value) => (value ? <LocalDateTime utcTime={value} /> : "—");

function Snapshot({ row }) {
  const entries = Object.entries(row ?? {});
  if (!entries.length) return <span className="text-xs text-text_muted">Nothing stored yet (read again at each send).</span>;
  return (
    <dl className="divide-y divide-border_soft rounded-lg border border-border">
      {entries.map(([key, value]) => (
        <div key={key} className="flex justify-between gap-3 px-3 py-1.5 text-xs">
          <dt className="shrink-0 font-mono text-[11px] text-text_muted">{key}</dt>
          <dd className="min-w-0 truncate text-right text-text_primary" title={formatValue(value)}>{formatValue(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

function Timeline({ deliveries }) {
  if (!deliveries?.length) return <span className="text-xs text-text_muted">No message yet.</span>;
  return (
    <ol className="space-y-1.5">
      {deliveries.map((d) => (
        <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-xs">
          <span className="min-w-0">
            <span className="font-medium text-text_primary">
              Send {d.send_no} · step {d.step_no} · {CHANNEL_LABEL[d.channel_key] ?? d.channel_key}
            </span>
            <span className="block truncate text-[11px] text-text_muted">
              {d.recipient_name || d.recipient_address || "—"} · <LocalDateTime utcTime={d.sent_at ?? d.created_date} />
            </span>
          </span>
          <StatusPill status={d.status} map={DELIVERY_STATUS} />
        </li>
      ))}
    </ol>
  );
}

// Read-only view of one case, with its record snapshot and the messages sent for it (fetched by the list page).
export default function InstanceViewDrawer({ record, deliveries, onClose }) {
  const sections = [
    {
      title: "Case",
      fields: [
        { label: "Rule", value: record.rule ? `${record.rule.name} (${record.rule.code})` : `#${record.rule_fid}` },
        { label: "Status", value: <StatusPill status={record.status} map={CASE_STATUS} /> },
        ...(record.status !== "OPEN" ? [{ label: "Why it closed", value: closeReasonText(record.resolved_reason) }, { label: "Closed", value: when(record.resolved_at) }] : []),
        { label: "About", value: record.subject_user ? `${record.subject_user.name}${record.subject_user.email ? ` (${record.subject_user.email})` : ""}` : subjectKeyText(record.subject_key) },
        ...(record.project ? [{ label: "Project", value: `${record.project.title} · Job ${record.project.file_id}` }] : []),
        { label: "Reference", value: <span className="font-mono">{record.reference_key}</span> },
        { label: "Trigger type", value: TRIGGER_TYPE_LABEL[record.trigger_type] ?? record.trigger_type },
        { label: "Opened", value: when(record.created_date) },
      ],
    },
    {
      title: "Progress",
      fields: [
        { label: "Current step", value: record.current_step },
        { label: "Sends on this step", value: record.step_send_count },
        { label: "Sends in all", value: record.total_send_count },
        { label: "First sent", value: when(record.first_sent_at) },
        { label: "Last sent", value: when(record.last_sent_at) },
        { label: "Next due", value: record.status === "OPEN" ? (record.next_due_at ? when(record.next_due_at) : "Watching (no more reminders)") : "—" },
        { label: "Rule version", value: `v${record.published_version_no}` },
        ...(record.error_count ? [{ label: "Engine errors", value: record.error_count }] : []),
      ],
    },
    { title: "Record at the last send", fields: [{ value: <Snapshot row={record.row_snapshot} /> }] },
    {
      title: `Messages (${record.delivery_count ?? deliveries?.length ?? 0})`,
      fields: [{ value: <Timeline deliveries={deliveries} /> }, ...(deliveries?.some((d) => d.status === "SKIPPED") ? [{ value: <p className="text-[11px] text-text_muted">Skipped messages say why in the Deliveries tab ({Object.values(SKIP_REASON_LABEL).slice(0, 3).join(", ").toLowerCase()}, ...).</p> }] : [])],
    },
  ];

  return <GlobalDetailDrawer open onClose={onClose} title={record.rule?.name ?? "Reminder case"} subtitle={`Case #${record.id}`} icon={<ListChecks size={20} />} sections={sections} />;
}
